import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import clsx from "clsx";
import { Button } from "./atoms";
import { ConfirmDeleteModal } from "./molecules/ConfirmDeleteModal";
import { deleteEmploymentType } from "../api/employmentTypes";
import {
    EMPTY_CONTRACT_INFO_FORM_VALUE,
    type MonthlyCondition,
    type WeekdayRule,
} from "./ContractInfoFormSection";

export type { MonthlyCondition, WeekdayRule };

/**
 * Step 間で引き回す雇用形態フォーム state（新規・編集共通）。
 * 契約情報タブ（Step 2）の入力値は現状 API には送らないが、UI 状態として保持する。
 * 契約情報の各フィールドは ContractInfoFormValue と同じ構造（共有コンポーネントで利用）。
 */
export interface EmploymentTypeFormState {
    code: string;
    name: string;
    /** 一日の所定労働時間 - 時間 */
    prescribedHours: number | "";
    /** 一日の所定労働時間 - 分 */
    prescribedMinutes: number | "";
    /** 週の休みの日数（0〜7） */
    weeklyDaysOff: number | "";
    /** 祝日を休みとするか（デフォルト true） */
    isHolidayOff: boolean;
    /** 月ごとの公休日数と勤務時間（12ヶ月分） */
    monthlyConditions: MonthlyCondition[];
    /** 曜日ごとの勤務形態（7日分） */
    weekdayRules: WeekdayRule[];
    /** 祝日専用の設定をするか */
    hasHolidaySettings: boolean;
    /** 祝日の勤務形態（hasHolidaySettings = true のときのみ有効） */
    holidayRule: WeekdayRule | null;
}

/** EmploymentTypeFormState の初期値（state 未確定時に使う） */
export const EMPTY_EMPLOYMENT_TYPE_FORM_STATE: EmploymentTypeFormState = {
    code: "",
    name: "",
    ...EMPTY_CONTRACT_INFO_FORM_VALUE,
};

/**
 * 子ページ（EmploymentTypeFormPage / EmploymentTypeTagFormPage など）が
 * 共通ヘッダーへエラー表示・state 共有するための Outlet context 型。
 */
export interface EmploymentTypeFormOutletContext {
    setPageError: (message: string | null) => void;
    invalidFields: string[];
    setInvalidFields: (fields: string[]) => void;
    pendingFormState: EmploymentTypeFormState | null;
    setPendingFormState: (state: EmploymentTypeFormState | null) => void;
}

/** 「基本情報」タブに含まれる必須フィールドのキー一覧 */
const BASIC_INFO_REQUIRED_FIELDS = ["code", "name"] as const;

interface MenuItem {
    id: string;
    label: string;
    path: string;
    displayLabel: string;
}

const EmploymentTypeEditLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams<{ id?: string }>();
    const isEdit = !!id;

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [pageError, setPageError] = useState<string | null>(null);
    const [invalidFields, setInvalidFields] = useState<string[]>([]);
    const [pendingFormState, setPendingFormState] = useState<EmploymentTypeFormState | null>(null);

    // 画面遷移時に子ページエラーを自動リセット
    useEffect(() => {
        setPageError(null);
    }, [location.pathname]);

    // 対象雇用形態が変わったら state をリセット
    useEffect(() => {
        setPendingFormState(null);
        setInvalidFields([]);
    }, [id, isEdit]);

    const menuItems = useMemo<MenuItem[]>(() => {
        const base = isEdit ? `/tenant/employment-type/${id}` : "/tenant/employment-type/new";
        const basicPath = isEdit ? `${base}/edit` : base;
        return [
            { id: "1", label: "基本情報", path: basicPath, displayLabel: "1. 基本情報" },
            { id: "2", label: "契約情報", path: `${base}/work-rules`, displayLabel: "2. 契約情報" },
            { id: "3", label: "タグ", path: `${base}/tags`, displayLabel: "3. タグ" },
        ];
    }, [id, isEdit]);

    const activeMenuId = useMemo(() => {
        const { pathname } = location;
        if (pathname.endsWith("/work-rules")) return "2";
        if (pathname.endsWith("/tags")) return "3";
        return "1";
    }, [location]);

    // タブの赤表示: invalidFields に含まれ、かつ pendingFormState でも未入力の場合に true
    const hasBasicInfoError = useMemo(() => {
        const basicKeys = BASIC_INFO_REQUIRED_FIELDS as readonly string[];
        return invalidFields.some((f) => {
            if (!basicKeys.includes(f)) return false;
            if (!pendingFormState) return true;
            const value = pendingFormState[f as keyof EmploymentTypeFormState];
            return !value || (typeof value === "string" && !value.trim());
        });
    }, [invalidFields, pendingFormState]);

    const handleDelete = async () => {
        if (!id) return;
        const typeId = parseInt(id, 10);
        if (Number.isNaN(typeId)) return;
        setDeleting(true);
        setDeleteError(null);
        try {
            await deleteEmploymentType(typeId);
            navigate("/tenant/employment-type");
        } catch (err) {
            console.error("Failed to delete employment type:", err);
            setDeleteError(err instanceof Error ? err.message : "削除に失敗しました");
            setDeleting(false);
        }
    };

    return (
        <div className="flex-1 flex flex-col overflow-hidden">
            {/* ヘッダー */}
            <div className="px-6 py-4 flex-shrink-0">
                <div className="flex items-center gap-3">
                    <div className="w-1 h-6 bg-brand-500 rounded-full" />
                    <h1 className="text-xl font-bold text-gray-800">
                        雇用形態の登録・編集
                    </h1>
                </div>
                {(deleteError || pageError) && (
                    <div className="mt-2 text-sm text-red-600">{deleteError ?? pageError}</div>
                )}
            </div>

            {/* フォームエリア */}
            <div className="flex-1 overflow-hidden px-6 pb-6">
                <div className="flex h-full gap-4">
                    {/* サイドメニュー */}
                    <div className="w-44 bg-white rounded-lg py-4 flex-shrink-0 flex flex-col">
                        <nav className="space-y-1 flex-1">
                            {menuItems.map((item) => {
                                const hasError = item.id === "1" && hasBasicInfoError;
                                return (
                                    <button
                                        key={item.id}
                                        type="button"
                                        onClick={() => navigate(item.path)}
                                        className={clsx(
                                            "w-full text-left px-4 py-2 text-sm",
                                            hasError
                                                ? "text-red-600 font-medium"
                                                : activeMenuId === item.id
                                                    ? "text-brand-500 font-medium"
                                                    : "text-gray-600 hover:text-gray-900"
                                        )}
                                    >
                                        {item.displayLabel}
                                    </button>
                                );
                            })}
                        </nav>
                        {isEdit && (
                            <div className="px-4 pt-4 mt-auto">
                                <Button
                                    variant="danger"
                                    size="md"
                                    onClick={() => setShowDeleteModal(true)}
                                    disabled={deleting}
                                    className="w-full"
                                >
                                    削除
                                </Button>
                            </div>
                        )}
                    </div>

                    <ConfirmDeleteModal
                        isOpen={showDeleteModal}
                        onCancel={() => setShowDeleteModal(false)}
                        onConfirm={() => {
                            setShowDeleteModal(false);
                            handleDelete();
                        }}
                        isDeleting={deleting}
                    />

                    {/* タブごとの内容 */}
                    <Outlet context={{
                        setPageError,
                        invalidFields,
                        setInvalidFields,
                        pendingFormState,
                        setPendingFormState,
                    } satisfies EmploymentTypeFormOutletContext} />
                </div>
            </div>
        </div>
    );
};

export default EmploymentTypeEditLayout;
